import { useEffect, useRef } from 'react';

interface UseRoomPollingOptions {
  /** Polling only runs while this is true. */
  enabled: boolean;
  /** Interval between polls in milliseconds. */
  intervalMs?: number;
  /** Called on each tick. The latest callback is always used. */
  onPoll: () => Promise<void>;
}

interface RoomPollingControls {
  /** Suspend polling without tearing down the timer. */
  pause: () => void;
  /** Resume polling after a pause. */
  resume: () => void;
}

/**
 * Polls at a fixed interval while enabled.
 *
 * The callback is kept in a ref so callers can close over fresh state without
 * restarting the timer, which also removes the need to mirror state into refs
 * just to read it from the interval. Overlapping polls are skipped, and the
 * timer is always cleared on unmount.
 */
export const useRoomPolling = ({
  enabled,
  intervalMs = 2000,
  onPoll,
}: UseRoomPollingOptions): RoomPollingControls => {
  const onPollRef = useRef(onPoll);
  const pausedRef = useRef(false);
  const inFlightRef = useRef(false);

  // 毎レンダーで最新のコールバックに差し替える（タイマーは張り直さない）
  onPollRef.current = onPoll;

  useEffect(() => {
    if (!enabled) return;

    const timer = setInterval(() => {
      if (pausedRef.current || inFlightRef.current) return;

      inFlightRef.current = true;
      onPollRef.current().finally(() => {
        inFlightRef.current = false;
      });
    }, intervalMs);

    return () => clearInterval(timer);
  }, [enabled, intervalMs]);

  return {
    pause: () => {
      pausedRef.current = true;
    },
    resume: () => {
      pausedRef.current = false;
    },
  };
};

export default useRoomPolling;
